import {useQuery} from '@apollo/client';
import {gql} from '@apollo/client';
import {GET_DRIVER_OPTIONS} from '../../../../graphql/queries/getDriverOptions';

/** 远程搜索触发的最小关键字长度；与客诉工作台保持一致 */
const DRIVER_SEARCH_MIN_LENGTH = 2;

// GraphQL查询定义 - 使用现有的接口
const GET_HUB_LIST = gql`
    query GetHubList {
        getHubList {
            id
            name
            sortingCode
        }
    }
`;

const GET_WAREHOUSE_LIST_FOR_POD_REVIEW = gql`
    query GetWarehouseListForPodReview($hubId: Long) {
        getWarehouseListForPodReview(hubId: $hubId) {
            id
            name
            sortingCode
            parentSiteId
        }
    }
`;

export const GET_AREAS_FOR_POD_REVIEW = gql`
    query GetAreasForPodReview($warehouseId: Long, $hubId: Long) {
        getAreasForPodReview(warehouseId: $warehouseId, hubId: $hubId) {
            id
            name
            sortingCode
            warehouseId
        }
    }
`;

export interface AreaForPodReviewType {
    id: string;
    name: string;
    sortingCode?: string;
    warehouseId?: string;
}

export interface GetAreasForPodReviewResponse {
    getAreasForPodReview: AreaForPodReviewType[];
}

export interface GetAreasForPodReviewVariables {
    warehouseId?: number;
    hubId?: number;
}

const GET_ALL_DSPS = gql`
    query GetAllDsps {
        getAllDsps {
            id
            name
            dspCode
        }
    }
`;

const GET_REVIEWERS = gql`
    query GetPodReviewReviewers {
        getPodReviewReviewers {
            id
            name
            phone
        }
    }
`;

// 选项数据类型
export interface OptionItem {
    id: string;
    name: string;
    code?: string;
    sortingCode?: string;
    hubId?: string;
    warehouseId?: string;
    dspId?: string;
}

export interface PodReviewOptionsResult {
    hubs: OptionItem[];
    warehouses: OptionItem[];
    areas: OptionItem[];
    dsps: OptionItem[];
    drivers: OptionItem[];
    reviewers: OptionItem[];
    loading: boolean;
    error?: any;
}

export const usePodReviewOptions = (
    selectedDspIds: string[] = [],
    /** 司机远程搜索关键字；未选 DSP 时候选集是全美司机，须靠关键字收窄 */
    driverKeyword = ''
): PodReviewOptionsResult => {
    // 查询Hub数据 - 总是查询全部
    const {data: hubsData, loading: hubsLoading, error: hubsError} = useQuery(GET_HUB_LIST);

    // 查询仓库数据 - 根据选中的Hub查询
    const {data: warehousesData, loading: warehousesLoading, error: warehousesError} = useQuery(
        GET_WAREHOUSE_LIST_FOR_POD_REVIEW,
        {
            variables: {hubId: null},
            skip: false // 总是执行查询，后端会处理null值
        }
    );

    // 查询区域数据 - 根据选中的Warehouse或Hub查询
    const {data: areasData, loading: areasLoading, error: areasError} = useQuery(
        GET_AREAS_FOR_POD_REVIEW,
        {
            variables: {
                warehouseId: null,
                hubId: null
            },
            skip: false // 总是执行查询，后端会处理null值
        }
    );

    // 查询DSP数据 - 总是查询全部
    const {data: dspsData, loading: dspsLoading, error: dspsError} = useQuery(GET_ALL_DSPS);

    /**
     * 司机数据双模式（与客诉工作台同一套 getDriverOptions 接口）：
     *   - 已选 DSP（单选/多选均走 dspIds）：候选集有限且后端不做条数截断，
     *     一次拉回交给下拉本地过滤
     *   - 未选 DSP：候选集是全美司机、后端只返回前 50 条，本地过滤会漏，
     *     故走关键字远程搜索，输入 >= 2 字才查
     */
    const isDriverRemoteMode = selectedDspIds.length === 0;
    const trimmedDriverKeyword = driverKeyword.trim();

    const {data: driverOptionsData, loading: driversLoading, error: driversError} = useQuery(
        GET_DRIVER_OPTIONS,
        {
            variables: {
                dspIds: isDriverRemoteMode ? null : selectedDspIds.map(id => parseInt(id, 10)),
                keyword: isDriverRemoteMode ? trimmedDriverKeyword : null,
            },
            // 远程模式关键字过短就不查，避免每敲一个字母打一次全美查询
            skip: isDriverRemoteMode && trimmedDriverKeyword.length < DRIVER_SEARCH_MIN_LENGTH,
        }
    );

    // 查询审核人数据
    const {data: reviewersData, loading: reviewersLoading, error: reviewersError} = useQuery(GET_REVIEWERS, {
        errorPolicy: 'ignore', // 忽略错误，避免影响其他查询
        onError: (error) => {
            console.warn('Failed to fetch reviewers:', error);
        }
    });

    // 转换数据格式
    const hubs: OptionItem[] = hubsData?.getHubList?.map((h: any) => ({
        id: h.id?.toString(),
        name: h.name,
        code: h.sortingCode,
        sortingCode: h.sortingCode
    })) || [];

    const warehouses: OptionItem[] = warehousesData?.getWarehouseListForPodReview?.map((w: any) => ({
        id: w.id?.toString(),
        name: w.name,
        code: w.sortingCode,
        sortingCode: w.sortingCode,
        hubId: w.parentSiteId?.toString() // 使用parentSiteId作为hubId
    })) || [];

    const areas: OptionItem[] = areasData?.getAreasForPodReview?.map((a: any) => ({
        id: a.sortingCode, // 使用sortingCode作为id
        name: a.name,
        code: a.sortingCode,
        sortingCode: a.sortingCode,
        warehouseId: a.warehouseId?.toString()
    })) || [];

    const dsps: OptionItem[] = dspsData?.getAllDsps?.map((d: any) => ({
        id: d.id?.toString(),
        name: d.name,
        code: d.dspCode,
        dspId: d.id?.toString()
    })) || [];

    const drivers: OptionItem[] = driverOptionsData?.getDriverOptions?.map((d: any) => ({
        id: d.accountId?.toString(),
        name: d.name,
        code: d.phone,
        dspId: d.dspId?.toString()
    })) || [];

    const reviewers: OptionItem[] = reviewersData?.getPodReviewReviewers?.map((r: any) => ({
        id: r.id?.toString(),
        name: r.name,
        code: r.phone
    })) || [];

    const loading = hubsLoading || warehousesLoading || areasLoading || dspsLoading || driversLoading || reviewersLoading;
    const error = hubsError || warehousesError || areasError || dspsError || driversError || reviewersError;

    return {
        hubs,
        warehouses,
        areas,
        dsps,
        drivers,
        reviewers,
        loading,
        error
    };
};